import {
  mapCondicoesPagamentoFromApi,
  getDefaultCondicaoPagamentoId,
  getDiscountPercentFromCondicao,
  findCondicaoPagamentoById,
} from "./paymentConditionUtils";
import {
  buildFamilyGrade,
  listGradeAgrupadores,
  listGradeSizes,
} from "../../shared/utils/family-grade";

const MARCA_NUM_TO_BRAND_KEY = {
  1: "karsten",
  2: "trussardi",
  3: "karsten_decor",
};

export { MARCA_NUM_TO_BRAND_KEY };

function getVitrineKey(vitrine) {
  return String(vitrine?.IdVitrineVirtualB2b ?? "");
}

function formatVitrineVigenciaLabel(vitrine) {
  const inicio =
    vitrine?.DataVigenciaInicioFormat || vitrine?.dataVigenciaInicioFormat || "";
  const fim =
    vitrine?.DataVigenciaFimFormat || vitrine?.dataVigenciaFimFormat || "";
  if (inicio && fim) return `${inicio} a ${fim}`;
  return inicio || fim || "";
}

function getCorFromApiProduct(product) {
  const corGrade = String(product.CorGrade ?? product.corGrade ?? "").trim();
  if (corGrade) return corGrade;

  const corApi = (product.Cor || "").trim();
  if (corApi) return corApi;

  const specs = product.Especificacoes || [];
  const spec = specs.find((s) =>
    /^cor$/i.test(String(s.DescricaoEspecificacao || "").trim()),
  );
  if (spec) {
    const v = `${spec.Valor1 || ""} ${spec.Valor2 || ""}`.trim();
    if (v) return v;
  }

  const fc = product.FotosPorCor?.[0]?.NomeCor;
  if (fc && String(fc).trim() && String(fc).trim() !== "Geral") {
    return String(fc).trim();
  }
  return "Geral";
}

function getSizeLabelFromProduct(product) {
  const tamanhoGrade = String(
    product.TamanhoGrade ?? product.tamanhoGrade ?? "",
  ).trim();
  if (tamanhoGrade) return tamanhoGrade;

  const fromApi = (product.Tamanho || "").trim();
  if (fromApi) return fromApi;

  const specs = product.Especificacoes || [];
  const tamExact = specs.find((s) =>
    /^TAMANHO$/i.test(String(s.DescricaoEspecificacao || "").trim()),
  );
  if (tamExact) {
    const v = `${tamExact.Valor1 || ""} ${tamExact.Valor2 || ""}`.trim();
    if (v) return v;
  }
  return "";
}

function displayProductName(product) {
  const grade = String(product.GradeVenda ?? product.gradeVenda ?? "").trim();
  if (grade) return grade;
  return (product.Nome || product.Descricao || product.NomeComercial || "").trim();
}

function deriveDisplayProductTitle(product) {
  const grade = String(product.GradeVenda ?? product.gradeVenda ?? "").trim();
  const subgrade = String(
    product.SubgradeVenda ?? product.subgradeVenda ?? "",
  ).trim();
  const cor = getCorFromApiProduct(product);
  const tamanho = getSizeLabelFromProduct(product);

  const parts = [grade, subgrade, cor !== "Geral" ? cor : "", tamanho].filter(
    Boolean,
  );
  if (parts.length > 0) return parts.join(" · ");

  return displayProductName(product) || "Produto";
}

function hasGradeMatrix(product) {
  return Boolean(
    String(product.GradeVenda ?? product.gradeVenda ?? "").trim() ||
      String(product.CorGrade ?? product.corGrade ?? "").trim() ||
      String(product.TamanhoGrade ?? product.tamanhoGrade ?? "").trim(),
  );
}

/**
 * Um card por Nome Comercial (GradeVenda) + Linha de Venda (WMW, ex.: CAMA, BANHO,
 * MESA). O nome comercial já vem completo com a cor (ex.: "Bossa Grafite"), então não
 * é preciso combinar com o campo Cor separado para separar cards por cor — mas o
 * mesmo nome comercial pode existir em mais de uma linha de venda ao mesmo tempo
 * (ex.: "Betina" tem itens de CAMA e de BANHO), o que juntava cama e toalha no mesmo
 * card. A Linha de Venda entra na chave para evitar essa mistura.
 */
function resolveFamilyKey(product, idProdutoFallback) {
  const grade = String(product?.GradeVenda ?? product?.gradeVenda ?? "").trim();
  if (grade) {
    const linhaVenda = String(
      product?.LinhaVenda ?? product?.linhaVenda ?? "",
    ).trim();
    const linhaPart = linhaVenda
      ? `|${linhaVenda.replace(/\s+/g, "_").toUpperCase()}`
      : "";
    return `GRD|${grade.replace(/\s+/g, "_").toUpperCase()}${linhaPart}`;
  }

  const k = String(product?.FamilyKey ?? product?.familyKey ?? "").trim();
  const id = idProdutoFallback ?? product?.IdProdutoString ?? "x";
  return k || `sku_${id}`;
}

// Um card de família agrega os SKUs de toda a grade, e cada SKU traz suas fotos.
// Sem limite, o carrossel de um único card chegava a dezenas de imagens em alta
// resolução — peso desnecessário de rede/memória para uma prévia de card.
const MAX_FOTOS_POR_FAMILIA = 8;

function mergeFotos(existing, product) {
  const urls = existing || [];
  if (urls.length >= MAX_FOTOS_POR_FAMILIA) return urls;

  const novas = (product.Fotos || []).filter(Boolean);
  if (novas.length === 0) return urls;

  const vistos = new Set(urls);
  const merged = [...urls];
  novas.forEach((u) => {
    if (merged.length >= MAX_FOTOS_POR_FAMILIA || vistos.has(u)) return;
    vistos.add(u);
    merged.push(u);
  });
  return merged;
}

function finalizeFamilyRecord(fam) {
  const products = fam.products || [];
  if (products.length === 0) return;

  const gradeVenda = String(
    products.find((p) => p.gradeVenda)?.gradeVenda || fam.gradeVenda || "",
  ).trim();
  // NomeComercial (WMW nmpdtven -> grade -> catálogo formatado, ver
  // B2bWmwProdutoTextoHelper) é mais confiável que "Produtos" genérico quando a
  // grade de venda não resolveu nome nenhum.
  const nomeComercial = String(
    products.find((p) => p.nomeComercial)?.nomeComercial || "",
  ).trim();
  const displayName = gradeVenda || nomeComercial || fam.name || "Produtos";

  fam.gradeVenda = gradeVenda;
  fam.commercialName = displayName;
  fam.productDisplayName = displayName;
  fam.name = displayName;

  if (fam.renderType !== "rows") {
    buildFamilyGrade(fam);
    const hasMatrix =
      (fam.grade?.sizes?.length ?? 0) > 0 &&
      Object.keys(fam.grade?.cells || {}).length > 0;
    if (!hasMatrix) fam.renderType = "rows";
  }
}

function discountPercentForItem(vitrine, item) {
  const pd = item?.PercentualDesconto ?? item?.percentualDesconto;
  if (pd != null && pd !== "") {
    const n = Number(pd);
    if (!Number.isNaN(n)) return n;
  }
  return Number(item.DescontoPercentual) || 0;
}

export function resultToFamiliesAndRules(apiResult) {
  const vitrines = apiResult?.Vitrines;
  const produtosList = apiResult?.Produtos;
  if (!Array.isArray(vitrines) || !Array.isArray(produtosList)) {
    return {
      families: {},
      businessRules: null,
      brandLabels: {},
      brandKeys: [],
      vitrinesMeta: {},
    };
  }

  const produtosByRef = {};
  produtosList.forEach((p) => {
    if (p?.IdProdutoString != null) produtosByRef[p.IdProdutoString] = p;
  });

  const businessRules = {
    global: { minOrderValue: 0, minOrderQty: 0, minInstallmentValue: 0 },
    vitrines: {},
    categories: {},
  };

  const families = {};
  const brandLabels = {};
  const vitrinesMeta = {};
  const marcaNames = { 1: "KARSTEN", 2: "TRUSSARDI", 3: "KARSTEN DECOR" };

  vitrines.forEach((vitrine) => {
    const vitrineKey = getVitrineKey(vitrine);
    const condicoesPagamento = mapCondicoesPagamentoFromApi(
      vitrine.CondicoesPagamento,
    );

    vitrinesMeta[vitrineKey] = {
      idVitrineVirtualB2b: vitrine.IdVitrineVirtualB2b,
      idVitrineVirtualB2bCliente: vitrine.IdVitrineVirtualB2bCliente ?? null,
      nome: vitrine.Nome || "",
      vigencia: formatVitrineVigenciaLabel(vitrine),
      condicoesPagamento,
      idCondicaoPagamentoPadrao: getDefaultCondicaoPagamentoId(
        condicoesPagamento,
        vitrine.IdCondicaoPagamento,
      ),
    };

    businessRules.vitrines[vitrineKey] = {
      minValue: Number(vitrine.ValorMinimo) || 0,
      minQty: Number(vitrine.QuantidadeMinimaPedido) || 0,
      minInstallmentValue: Number(vitrine.DuplicataMinima) || 0,
      idVitrineVirtualB2b: vitrine.IdVitrineVirtualB2b,
      idVitrineVirtualB2bCliente: vitrine.IdVitrineVirtualB2bCliente ?? null,
      nome: vitrine.Nome || "",
    };

    const brandKey =
      vitrine.Marca != null
        ? MARCA_NUM_TO_BRAND_KEY[vitrine.Marca] || "outros"
        : "outros";

    if (!brandLabels[brandKey]) {
      brandLabels[brandKey] =
        marcaNames[vitrine.Marca] || brandKey.toUpperCase();
    }

    (vitrine.Itens || []).forEach((item) => {
      const product = produtosByRef[item.IdProdutoString];
      if (!product) return;

      const withGrade = hasGradeMatrix(product);
      const familyKey = resolveFamilyKey(product, item.IdProdutoString);

      if (!families[brandKey]) families[brandKey] = {};
      const byFamily = families[brandKey];

      if (!byFamily[familyKey]) {
        const gradeVenda = String(
          product.GradeVenda ?? product.gradeVenda ?? "",
        ).trim();
        const nomeComercial = String(
          product.NomeComercial ?? product.nomeComercial ?? "",
        ).trim();
        const linhaVenda = String(
          product.LinhaVenda ?? product.linhaVenda ?? "",
        ).trim();
        const displayName =
          gradeVenda ||
          nomeComercial ||
          (product.Colecao || "").trim() ||
          "Produtos";
        byFamily[familyKey] = {
          collectionName: (product.Colecao || "").trim(),
          nomeColecao: (product.NomeColecao || "").trim(),
          gradeVenda,
          linhaVenda,
          // Ativa o subtítulo "da linha X" do card (getFamilyLineLabel em
          // ProductList.js) para diferenciar visualmente cards com o mesmo nome
          // comercial em linhas de venda diferentes (ex.: "Betina" cama vs banho).
          lineName: linhaVenda,
          commercialName: displayName,
          productDisplayName: displayName,
          name: displayName,
          vitrine: vitrineKey,
          vitrineDisplayName: (vitrine.Nome || "").trim(),
          vitrineVigencia: formatVitrineVigenciaLabel(vitrine),
          condicoesPagamento,
          idCondicaoPagamentoPadrao: getDefaultCondicaoPagamentoId(
            condicoesPagamento,
            vitrine.IdCondicaoPagamento,
          ),
          renderType: withGrade ? "card" : "rows",
          status: product.Status || "A",
          category: product.Categoria || "",
          subcategory: product.Subcategoria || "",
          products: [],
          fotos: mergeFotos([], product),
          grade: { sizes: [], groups: [], cells: {} },
        };
      } else {
        byFamily[familyKey].fotos = mergeFotos(
          byFamily[familyKey].fotos,
          product,
        );
      }

      const productId = `${item.IdVitrineVirtualB2bItem}_${item.IdProdutoString}`;
      const displayTitle = deriveDisplayProductTitle(product);
      const defaultCondId = getDefaultCondicaoPagamentoId(
        condicoesPagamento,
        vitrine.IdCondicaoPagamento,
      );
      const baseDiscount = discountPercentForItem(vitrine, item);

      byFamily[familyKey].products.push({
        id: productId,
        idProduto: Number(product.IdProduto) || product.IdProdutoString,
        name: displayTitle,
        fullName: displayProductName(product),
        displayTitle,
        corLabel: getCorFromApiProduct(product),
        corHex: (product.CorHex || "").trim() || null,
        gradeVenda: String(product.GradeVenda ?? product.gradeVenda ?? "").trim(),
        nomeComercial: String(
          product.NomeComercial ?? product.nomeComercial ?? "",
        ).trim(),
        subgradeVenda: String(
          product.SubgradeVenda ?? product.subgradeVenda ?? "",
        ).trim(),
        corGrade: getCorFromApiProduct(product),
        linhaGrade: String(product.LinhaGrade ?? product.linhaGrade ?? "").trim(),
        colunaGrade: String(product.ColunaGrade ?? product.colunaGrade ?? "").trim(),
        tamanhoGrade: getSizeLabelFromProduct(product),
        agrupador: String(product.Agrupador ?? product.agrupador ?? "").trim(),
        linhaVenda: String(product.LinhaVenda ?? product.linhaVenda ?? "").trim(),
        ref: product.IdProdutoString || "",
        estoque: product.Estoque ?? product.estoque ?? null,
        price: Number(item.Valor) || 0,
        discount: getDiscountPercentFromCondicao(
          findCondicaoPagamentoById(condicoesPagamento, defaultCondId),
          baseDiscount,
        ),
        multiple: Number(product.Multiplo) > 0 ? Number(product.Multiplo) : 1,
        images: product.Fotos || [],
        especificacoes: product.Especificacoes || [],
        vitrine: vitrineKey,
        idVitrineVirtualB2b: vitrine.IdVitrineVirtualB2b,
        idVitrineVirtualB2bCliente: vitrine.IdVitrineVirtualB2bCliente ?? null,
        vitrineName: vitrine.Nome || "",
        condicoesPagamento,
        idCondicaoPagamentoPadrao: defaultCondId,
      });
    });
  });

  Object.keys(families).forEach((bk) => {
    Object.keys(families[bk]).forEach((fk) => {
      finalizeFamilyRecord(families[bk][fk]);
    });
  });

  const brandKeys = Object.keys(families);
  brandKeys.forEach((k) => {
    if (!brandLabels[k]) brandLabels[k] = k.toUpperCase();
  });

  return { families, businessRules, brandLabels, brandKeys, vitrinesMeta };
}

export { listGradeAgrupadores, listGradeSizes };
